
import { handleGetUserWallets } from '../userDashboard/userControllers/profileController';

export interface Wallet {
    currency: string;
    balance: number;
    country_code?: string;
    status?: string | number;
    [key: string]: unknown;
}

export interface WalletPayloadObject {
    wallets: Wallet[];
    primary_currency?: string | number;
    [key: string]: unknown;
}

export type WalletPayload = WalletPayloadObject | Wallet[];

export interface DashboardMetrics {
    games_played?: number;
    games_paid?: number;
    no_shows?: number;
    total_spent?: number;
}

export interface ProfileMetadata {
    matches_played?: number;
    win_rate?: number;
    total_goals?: number;
    currency?: string;
    payin_currency?: string;
    [key: string]: unknown;
}

export interface Plan {
    name?: string;
    [key: string]: unknown;
}

interface CountryDetailsShape {
    iso3?: string;
    iso2?: string;
    name?: string;
    iso_3?: string;
    iso_2?: string;
    code?: string;
    [key: string]: unknown;
}

export interface Profile {
    id?: string | number;
    name?: string;
    firstname?: string;
    lastname?: string;
    username?: string;
    email?: string;
    image?: string;
    country_code?: string;
    countryCode?: string;
    currency?: string;
    created_at?: string | Date;
    active_currency?: string;
    balance?: number;
    wallet_balance?: number | string;
    wallets?: Wallet[];
    wallet?: Partial<Wallet> & { balance?: number; country_code?: string };
    plan?: Plan;
    dashboard_metrics?: DashboardMetrics;
    metadata?: ProfileMetadata;
    can_perform_organizer_actions?: boolean;
    role?: string;
    has_active_organizer_status?: boolean;
    organizer_access_level?: string;
    country?: CountryDetailsShape;
    state?: string;
    city?: string;
    address?: string;
    phone?: string;
    has_pin?: boolean;
    paystack_recipient_code?: string;
    [key: string]: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const extractWalletPayload = (response: unknown): WalletPayload | null => {
    if (!isRecord(response)) return null;

    const dataLayer = response.data;
    if (isRecord(dataLayer) && (Array.isArray(dataLayer.data) || isRecord(dataLayer.data))) {
        return dataLayer.data as WalletPayload;
    }

    if (Array.isArray(dataLayer) || isRecord(dataLayer)) {
        return dataLayer as WalletPayload;
    }

    return null;
};

const normalizeWalletPayload = (payload: WalletPayload | null): { wallets: Wallet[]; primaryCurrency: string | null } => {
    if (!payload) {
        return {
            wallets: [],
            primaryCurrency: null,
        };
    }

    const wallets = Array.isArray(payload)
        ? payload
        : payload?.wallets || [];

    const primaryCurrency = (!Array.isArray(payload) && payload?.primary_currency)
        ? String(payload.primary_currency).toUpperCase()
        : null;

    return { wallets, primaryCurrency };
};

export const hydrateProfileWithWalletPayload = ({
    profile,
    payload,
    preferredCurrency,
}: {
    profile?: Profile;
    payload: WalletPayload | null;
    preferredCurrency?: string | null;
}): Profile => {
    if (!payload) {
        return profile || {};
    }

    const { wallets, primaryCurrency } = normalizeWalletPayload(payload);
    if (!wallets.length) {
        return profile || {};
    }

    const firstWallet = wallets[0];
    if (!firstWallet) {
        return profile || {};
    }

    const normalizedCurrency = (
        preferredCurrency ||
        profile?.active_currency ||
        primaryCurrency ||
        firstWallet.currency ||
        ''
    )
        .toString()
        .toUpperCase();

    const targetWallet =
        wallets.find(
            (wallet) =>
                String(wallet.currency).toUpperCase() === normalizedCurrency
        ) || firstWallet;

    const normalizedBalance = Number(targetWallet.balance ?? 0);

    return {
        ...(profile || {}),
        active_currency: targetWallet.currency,
        balance: normalizedBalance,
        wallets,
        wallet: {
            ...(profile?.wallet || {}),
            ...targetWallet,
            balance: normalizedBalance,
        },
    };
};

export const refreshActiveWalletSnapshot = async ({
    currency,
    saveUserProfile,
    userProfile,
}: {
    currency?: string | null;
    saveUserProfile: (profile: Profile) => void;
    userProfile?: Profile;
}) => {
    if (typeof saveUserProfile !== 'function') {
        return;
    }

    try {
        const response = await handleGetUserWallets();
        const payload = extractWalletPayload(response);
        if (!payload) {
            return;
        }

        const hydratedProfile = hydrateProfileWithWalletPayload({
            profile: userProfile || {},
            payload,
            preferredCurrency: currency ?? null,
        });

        saveUserProfile(hydratedProfile);
    } catch (error) {
        console.warn('Unable to refresh wallet snapshot', error);
    }
};

export const fetchWalletPayload = async (): Promise<WalletPayload | null> => {
    const response = await handleGetUserWallets();
    return extractWalletPayload(response);
};
